/**
 * Product Service
 * Handles product and batch management API calls
 */

import { api } from '../lib/axios';
import { LabelDesign } from '../types/enhanced-product-info.types';

export interface ProductBatch {
  id: string;
  manufacturerId: string;
  productName: string;
  batchTitle?: string;
  productCode?: string;
  unitCode?: string;
  processVersion?: string;
  eventDate?: string;
  batchSequence?: number;
  serialStart: number;
  serialEnd: number;
  createdAt: string;
  labelDesign?: LabelDesign | null;
  innerQrEnabled?: boolean;
  _count?: {
    products: number;
  };
}

export interface Product {
  id: string;
  batchId: string;
  serialNumber: number;
  outerQrToken: string;
  innerQrToken: string | null;
  currentOwnerId: string | null;
  activationStatus: string;
  status: string;
  consumedAt: string | null;
  createdAt: string;
}

export interface CreateBatchData {
  manufacturerId: string;
  productName: string;
  productCode: string;
  unitCode: string;
  eventDate: string;
  serialStart: number;
  serialEnd: number;
  qrTokenFormat?: 'legacy' | 'short';
  qrTokenLength?: number;
  labelDesign?: LabelDesign;
  processVersion?: string;
  includeInnerQr?: boolean;
}

export interface BatchTitleOption {
  id: string;
  code: string;
  name?: string | null;
  unitType?: string | null;
}

export interface BatchTitleOptions {
  productCodes: BatchTitleOption[];
  unitCodes: BatchTitleOption[];
  processVersions: BatchTitleOption[];
}

export interface BatchTitleSettingsCode extends BatchTitleOption {
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface BatchTitleSettings {
  productCodes: BatchTitleSettingsCode[];
  unitCodes: BatchTitleSettingsCode[];
  processVersions: BatchTitleSettingsCode[];
}

export type BatchTitleCodeType = 'product' | 'unit' | 'process';

export interface CreateBatchTitleCodePayload {
  code: string;
  name?: string;
  unitType?: string;
}

export interface UpdateBatchTitleCodePayload {
  name?: string;
  unitType?: string;
  isActive?: boolean;
}

class ProductService {
  /**
   * Create a new product batch
   */
  async createBatch(data: CreateBatchData): Promise<{ success: boolean; batch?: ProductBatch; error?: string }> {
    const response = await api.post('/products/batch', data);
    return response.data;
  }

  /**
   * Get batch title reference data (product/unit/process codes)
   */
  async getBatchTitleOptions(): Promise<BatchTitleOptions> {
    const response = await api.get('/products/batch-title/options');
    return response.data.data;
  }

  /**
   * Get batch title code settings (includes inactive codes)
   */
  async getBatchTitleSettings(): Promise<BatchTitleSettings> {
    const response = await api.get('/products/batch-title/settings');
    return response.data.data;
  }

  /**
   * Create a batch title code
   */
  async createBatchTitleCode(type: BatchTitleCodeType, payload: CreateBatchTitleCodePayload) {
    const endpoint =
      type === 'product'
        ? '/products/batch-title/product-codes'
        : type === 'unit'
          ? '/products/batch-title/unit-codes'
          : '/products/batch-title/process-versions';
    const response = await api.post(endpoint, payload);
    return response.data.data;
  }

  /**
   * Update a batch title code
   */
  async updateBatchTitleCode(
    type: BatchTitleCodeType,
    id: string,
    payload: UpdateBatchTitleCodePayload
  ) {
    const endpoint =
      type === 'product'
        ? `/products/batch-title/product-codes/${id}`
        : type === 'unit'
          ? `/products/batch-title/unit-codes/${id}`
          : `/products/batch-title/process-versions/${id}`;
    const response = await api.patch(endpoint, payload);
    return response.data.data;
  }

  /**
   * Get batch details
   */
  async getBatch(batchId: string): Promise<ProductBatch> {
    const response = await api.get(`/products/batch/${batchId}`);
    return response.data.batch || response.data.data?.batch || response.data;
  }

  /**
   * Get manufacturer batches
   */
  async getManufacturerBatches(manufacturerId: string, limit: number = 50): Promise<ProductBatch[]> {
    const response = await api.get(`/products/manufacturer/${manufacturerId}/batches`, {
      params: { limit }
    });
    if (response.data?.success === false) {
      throw new Error(response.data?.error || response.data?.message || 'Failed to load batches');
    }
    const batches = response.data?.batches || response.data?.data?.batches || response.data;
    return Array.isArray(batches) ? batches : [];
  }

  /**
   * Get products in a batch
   */
  async getProductsByBatch(batchId: string, limit: number = 100): Promise<Product[]> {
    const response = await api.get(`/products/batch/${batchId}/products`, {
      params: { limit }
    });
    return response.data.products || response.data.data?.products || response.data;
  }

  /**
   * Get manufacturer inventory
   */
  async getManufacturerInventory(
    manufacturerId: string,
    filters?: {
      status?: string;
      activationStatus?: string;
      batchId?: string;
    }
  ): Promise<Product[]> {
    const response = await api.get(`/products/manufacturer/${manufacturerId}/inventory`, {
      params: filters
    });
    return response.data.products || response.data.data?.products || response.data;
  }

  /**
   * Activate a single product
   */
  async activateProduct(productId: string): Promise<void> {
    await api.post(`/products/${productId}/activate`);
  }

  /**
   * Bulk activate products
   */
  async bulkActivateProducts(productIds: string[]): Promise<{ success: boolean; activated: number }> {
    const response = await api.post('/products/activate/bulk', { productIds });
    return response.data;
  }

  /**
   * Download batch QR codes as PDF
   */
  async downloadBatchQRCodes(batchId: string): Promise<void> {
    const token = localStorage.getItem('auth_token');
    const url = `${api.defaults.baseURL}/products/batch/${batchId}/qr-codes/pdf`;
    
    try {
      console.log('Downloading batch QR codes from:', url);
      console.log('Using token:', token ? 'Present' : 'Missing');
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf'
        }
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Download failed:', response.status, errorText);
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      console.log('Blob size:', blob.size, 'Type:', blob.type);

      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }

      // Create download link
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `batch-${batchId}-qr-codes.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      console.log('Download completed successfully');
    } catch (error) {
      console.error('Download error:', error);
      throw error;
    }
  }

  /**
   * Download hierarchical QR codes (case or pallet level)
   */
  async downloadHierarchicalQRs(batchId: string, level: 'case' | 'pallet'): Promise<void> {
    const token = localStorage.getItem('auth_token');
    const url = `${api.defaults.baseURL}/products/batch/${batchId}/qr-codes/hierarchical?level=${level}`;
    
    try {
      console.log(`Downloading ${level} QR codes from:`, url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf'
        }
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Download failed:', response.status, errorText);
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      console.log('Blob size:', blob.size, 'Type:', blob.type);

      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }

      // Create download link
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `batch-${batchId}-${level}-qr-codes.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      console.log(`${level} download completed successfully`);
    } catch (error) {
      console.error(`${level} download error:`, error);
      throw error;
    }
  }

  /**
   * Download single product QR codes as PDF
   */
  async downloadProductQRCodes(productId: string): Promise<void> {
    const token = localStorage.getItem('auth_token');
    const url = `${api.defaults.baseURL}/products/${productId}/qr-codes/pdf`;
    
    try {
      console.log('Downloading product QR codes from:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf'
        }
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Download failed:', response.status, errorText);
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      console.log('Blob size:', blob.size, 'Type:', blob.type);

      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }

      // Create download link
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `product-${productId}-qr-codes.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      console.log('Product download completed successfully');
    } catch (error) {
      console.error('Product download error:', error);
      throw error;
    }
  }

  /**
   * Delete a batch (allowed within 24 hours)
   */
  async deleteBatch(batchId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    const response = await api.delete(`/products/batch/${batchId}`);
    return response.data;
  }

  /**
   * Search batches by product name
   */
  async searchBatches(query: string): Promise<ProductBatch[]> {
    const response = await api.get('/products/batches/search', {
      params: { q: query, limit: 20 }
    });
    return response.data.batches || response.data.data?.batches || response.data;
  }
}

export const productService = new ProductService();
